import { getTokenFromCookie } from './cookies'

type JwtNumericDate = number | string

type JwtRecord = Record<string, unknown>

const JWT_SEGMENT_COUNT = 3

/**
 * Decode a Base64Url-encoded JWT segment to a UTF-8 string.
 * Returns an empty string on any decoding failure.
 */
const decodeBase64Url = (input: string): string => {
  if (typeof atob !== 'function') {
    return ''
  }

  const normalized = input.replace(/-/g, '+').replace(/_/g, '/')
  const padding = (4 - (normalized.length % 4)) % 4
  const padded = normalized + '='.repeat(padding)

  try {
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return new TextDecoder().decode(bytes)
  } catch {
    // Intentionally ignored: malformed input must not throw.
  }

  return ''
}

/**
 * Safely extract the payload object from a JWT string.
 * Returns null for malformed/non-JWT strings or invalid JSON.
 */
const toNumericDateSeconds = (value: JwtNumericDate): null | number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 ? Math.floor(value) : null
  }

  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null
  }

  return null
}

export const getTokenPayload = (token: string): JwtRecord | null => {
  if (typeof token !== 'string' || token.length === 0) return null

  const parts = token.split('.')
  if (parts.length !== JWT_SEGMENT_COUNT) return null

  const payloadText = decodeBase64Url(parts[1])
  if (payloadText.length === 0) return null

  try {
    const parsed = JSON.parse(payloadText)
    return parsed !== null && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export const getTokenExpiration = (token: string): null | number => {
  const payload = getTokenPayload(token)
  if (payload === null) return null

  const rawExp = payload.exp
  if (typeof rawExp !== 'number' && typeof rawExp !== 'string') return null

  return toNumericDateSeconds(rawExp)
}

export const isTokenExpired = (token: string, now: number = Date.now()): boolean => {
  const exp = getTokenExpiration(token)

  if (exp === null) {
    return true
  }

  return now >= exp * 1000
}

export const getTokenSubject = (token: string): null | string => {
  const payload = getTokenPayload(token)
  if (payload === null) return null

  const subject = payload.sub

  if (typeof subject === 'number' || typeof subject === 'string') {
    return String(subject)
  }

  return null
}

/**
 * Read the current auth token payload from the cookie, if present and valid.
 */
export const getCookieTokenPayload = (): null | Record<string, unknown> => {
  const token = getTokenFromCookie()
  return token ? getTokenPayload(token) : null
}

/**
 * Determine whether a token represents an active impersonation session.
 * Malformed tokens are treated as non-impersonating.
 */
export const isImpersonationToken = (token: string): boolean => {
  return getImpersonatorId(token) !== null
}

/**
 * Extract the impersonator_id claim from a token.
 * Returns null for non-impersonation or malformed tokens.
 */
export const getImpersonatorId = (token: string): null | number | string => {
  const payload = getTokenPayload(token)
  if (payload === null) return null

  const id = payload.impersonator_id
  if (typeof id !== 'number' && typeof id !== 'string') return null

  const subject = payload.sub
  if (
    (typeof subject === 'number' || typeof subject === 'string') &&
    String(subject) === String(id)
  ) {
    return null
  }

  return id
}

export const isTokenExpiredWithGrace = (token: string, graceSeconds: number = 0): boolean => {
  const exp = getTokenExpiration(token)

  if (exp === null) {
    return true
  }

  const thresholdMs = (exp + graceSeconds) * 1000
  return Date.now() >= thresholdMs
}
